import { request } from './request'
import {
  AiDebugResponse,
  AssistantChatMessageFeedbackPayload,
  AssistantConversationOut,
  AssistantDialoguesOut,
  CreateAssistantChatMessageFeedbackPayload,
  CreateFixedPromptTextGenerationOut,
  GenerateAssistantChatMessagePayload,
  GetDialogueSnapshotResponse,
  GetFixedPromptTextGenerationOut,
  PlaygroundUsersOut,
  SupportTextGenerationStatusOut,
  UserOut,
} from './types'
import { camelCaseToSnake } from './utils/camelCaseToSnake'

const appendListOfIdsToParams = (paramName: string, ids: string[]) => {
  const params = new URLSearchParams()

  for (const id of ids) {
    params.append(paramName, id)
  }

  return params.toString()
}

export const apiClient = {
  signInComplete(payload: {
    access_token: string
    id_token: string
    refresh_token: string
    client_id: string
  }) {
    return request('/signin/complete', {
      payload,
      init: { method: 'post' },
    })
  },
  signOut() {
    return request('/signout', { init: { method: 'post' } })
  },
  whoami() {
    return request<UserOut>('/whoami')
  },

  // only used to get corresponding correlation ID to redirect from legacy route /debug/:id
  getSupportTextGeneration(messageId: string) {
    return request<SupportTextGenerationStatusOut>(
      `/support-text-generation/${messageId}`,
      {
        convertToCamelCase: true,
      },
    )
  },

  getAiDebug(correlationIds: string[], callId: string | null) {
    const params = callId
      ? `&call_id=${callId}`
      : appendListOfIdsToParams('correlation_id', correlationIds)

    return request<AiDebugResponse>(`/debug?${params}`, {
      convertToCamelCase: true,
    })
  },

  getSnapshot(
    correlationIds: string[],
    callId: string | null,
    getLatest: boolean = false,
  ) {
    const params = callId
      ? `&call_id=${callId}`
      : appendListOfIdsToParams('correlation_id', correlationIds)
    return request<GetDialogueSnapshotResponse>(
      `/debug/dialogue-snapshot?${params}${getLatest ? '&latest=true' : ''}`,
      {
        convertToCamelCase: true,
      },
    )
  },

  createFixedPromptTextGeneration(payload: {
    support_text_generation_id: string
    correlation_id: string
    prompt_content: string
  }) {
    return request<CreateFixedPromptTextGenerationOut>('/fixed-prompt-text-generation', {
      payload,
      init: { method: 'POST' },
      convertToCamelCase: true,
    })
  },
  getFixedPromptTextGeneration(fixedPromptTextGenerationId: string) {
    return request<GetFixedPromptTextGenerationOut>(
      `/fixed-prompt-text-generation/${fixedPromptTextGenerationId}`,
      {
        convertToCamelCase: true,
      },
    )
  },

  getLatestAssistantChatMessage() {
    return request<AssistantConversationOut>(
      `/playground/assistant-text-generation?latest=true`,
      {
        convertToCamelCase: true,
      },
    )
  },

  createNewCustomerMessage(payload: GenerateAssistantChatMessagePayload) {
    const convertedPayload = camelCaseToSnake(payload)
    return request<{ assistantTextGenerationId: string }>(
      `/playground/assistant-text-generation`,
      {
        payload: convertedPayload,
        init: { method: 'POST' },
        convertToCamelCase: true,
      },
    )
  },

  getPlaygroundUsers() {
    return request<PlaygroundUsersOut[]>(`/playground/users`, {
      convertToCamelCase: true,
    })
  },

  getAssistantDialoguesList() {
    return request<AssistantDialoguesOut>(`/playground/dialogues`, {
      convertToCamelCase: true,
    })
  },

  getAssistantMessagesFeedback(messageIds: string[]) {
    return request<AssistantChatMessageFeedbackPayload>(
      `/playground/assistant-feedback?${appendListOfIdsToParams(
        'assistant_generation_id',
        messageIds,
      )}`,
      {
        convertToCamelCase: true,
      },
    )
  },

  createAssistantMessageFeedback(payload: CreateAssistantChatMessageFeedbackPayload) {
    const convertedPayload = camelCaseToSnake(payload)
    return request<{ assistantTextGenerationId: string }>(
      `/playground/assistant-feedback`,
      {
        payload: convertedPayload,
        init: { method: 'POST' },
        convertToCamelCase: true,
      },
    )
  },
}
